/* eslint-disable no-param-reassign */
interface IData {
  event: string;
  url: string;
  type: string;
  status: string;
  openInPopup: boolean;
}

export const linksHandler = (
  editor: any,
  lastRange: Range | null,
  selectedText: string | null,
  data: IData,
  designSystem: {
    isHyperLinkBold: boolean;
    isHyperLinkUnderline: boolean;
    isHyperLinkItalic: boolean;
  },
) => {
  const selection = window.getSelection();
  if (!selection) return;

  const { url, event, type, status, openInPopup } = data;

  if (selection && lastRange) {
    selection.removeAllRanges();
    selection.addRange(lastRange);
  }

  if (selection.focusNode && !selectedText) {
    if (selection.focusNode.parentElement instanceof HTMLElement) {
      let aEl: HTMLElement | null = null;
      if (selection.focusNode.parentElement.nodeName === "A") {
        aEl = selection.focusNode.parentElement;
      } else aEl = selection.focusNode.parentElement.closest("a");

      if (aEl) {
        if (status === "remove") {
          const range = new Range();
          range.selectNode(aEl);
          selection.removeAllRanges();
          selection.addRange(range);
        } else {
          const sanitized =
            type === "anchor"
              ? "javascript:void(0);"
              : url.startsWith("http") ||
                  url.startsWith("mailto:") ||
                  url.startsWith("tel:")
                ? url
                : `https://${url}`;

          aEl.setAttribute("href", sanitized);
          if (type === "anchor") {
            aEl.removeAttribute("target");
            aEl.removeAttribute("data-open-in-popup");
            aEl.setAttribute("data-anchor-trigger", url);
          } else {
            aEl.removeAttribute("data-anchor-trigger");
            aEl.setAttribute("target", "_blank");
            aEl.setAttribute(
              "data-open-in-popup",
              openInPopup ? "true" : "false",
            );
            if (event) {
              aEl.setAttribute("data-analytics-click-event", event);
            }
          }
          return;
        }
      }
    }
  }

  editor.link.remove();
  removeLink(selection);

  if (!url || status === "remove" || !selectedText) return;

  const getURLFormat = (url: string) => {
    if (
      url.startsWith("http") ||
      url.startsWith("mailto:") ||
      url.startsWith("tel:")
    )
      return url;
    return `https://${url}`;
  };

  const sanitized =
    type === "anchor" ? "javascript:void(0);" : getURLFormat(url);
  if (selection && lastRange) {
    const range = selection.getRangeAt(0);
    const fragment = range.cloneContents();
    const nodes = fragment.querySelectorAll("*");
    nodes.forEach((node: Node) => {
      if ((node as HTMLElement)?.style) {
        (node as HTMLElement).style.removeProperty("color");
      }
    });
    const aEl = document.createElement("a");

    aEl.setAttribute("href", sanitized);
    if (type === "hyperlink") {
      aEl.setAttribute("data-open-in-popup", openInPopup ? "true" : "false");
      aEl.setAttribute("aria-label", `Hyperlink to ${url}`);
    }
    if (event && type !== "anchor") {
      aEl.setAttribute("data-analytics-click-event", event);
      aEl.setAttribute("aria-label", `Anchor link to ${url}`);
    }
    if (type === "anchor") {
      aEl.setAttribute("data-anchor-trigger", url);
    } else {
      aEl.setAttribute("target", "_blank");
    }
    const div = document.createElement("div");
    div.appendChild(fragment);

    // Create a wrapper element to hold all styles
    let styledContent = div.innerHTML;

    if (designSystem.isHyperLinkBold) {
      styledContent = `<strong>${styledContent}</strong>`;
    }

    if (designSystem.isHyperLinkUnderline) {
      styledContent = `<u>${styledContent}</u>`;
    }

    if (designSystem.isHyperLinkItalic) {
      styledContent = `<em>${styledContent}</em>`;
    }

    aEl.innerHTML = styledContent;
    range.deleteContents();
    range.insertNode(aEl);
  }
};

export const getLinkValues = (el: HTMLElement) => {
  const isLinkActive = el.nodeName !== "A" ? el.closest("a") : true;
  if (!isLinkActive) {
    return {
      type: "hyperlink",
      event: "",
      url: "",
      status: "create",
    };
  }
  if (isLinkActive instanceof HTMLElement) el = isLinkActive;

  const url = el.getAttribute("href") ?? "";
  const anchor = el.getAttribute("data-anchor-trigger");
  let type: "anchor" | "hyperlink";
  if (
    url &&
    (url.startsWith("http") ||
      url.startsWith("mailto:") ||
      url.startsWith("tel:"))
  ) {
    type = "hyperlink";
  } else type = "anchor";
  return {
    type: url
      ? url.startsWith("http") ||
        url.startsWith("mailto:") ||
        url.startsWith("tel:")
        ? "hyperlink"
        : "anchor"
      : "anchor",
    event: el.getAttribute("data-analytics-click-event") || "",
    url:
      type === "hyperlink"
        ? url.replace(/javascript(:|%3A)void\(0\)/gm, "")
        : `#${anchor}`,
    status: "update",
    openInPopup: el.getAttribute("data-open-in-popup") === "true",
  };
};

const removeLink = (selection: Selection | null) => {
  if (!selection || !selection.rangeCount) return;
  try {
    const container = document.createElement("div");
    for (let i = 0, len = selection.rangeCount; i < len; ++i) {
      const range = selection.getRangeAt(i).cloneRange();

      // Tripple click selection fix
      if (
        range.startContainer !== range.endContainer &&
        range.endOffset === 0
      ) {
        range.setEndAfter(range.startContainer);
      }

      container.appendChild(range.cloneContents());
    }
    const links = container.getElementsByTagName("a");

    if (links.length === 0) return;

    for (let i = links.length - 1; i >= 0; --i) {
      const link = links[i];
      const parent = link.parentNode as HTMLElement;
      while (link.firstChild) {
        if (parent.contains(link)) parent?.insertBefore(link.firstChild, link);
        else break;
      }
      const clone = link.cloneNode(true);
      if (parent.contains(link)) parent.replaceChild(clone, link);
    }
    const frag = document.createDocumentFragment();
    const childNodes = container.childNodes;
    for (let i = 0, len = childNodes.length; i < len; ++i) {
      frag.appendChild(childNodes[i].cloneNode(true));
    }
    selection.getRangeAt(0).deleteContents();
    selection.getRangeAt(0).insertNode(frag);
  } catch (e) {}
};

export const getLinkInParents = (
  el: HTMLElement | null,
): HTMLElement | null => {
  while (el) {
    if (el.id.includes("froala-editor")) {
      return null;
    }

    if (el.nodeType === 1 && el.nodeName === "A") {
      return el;
    }

    if (!el.parentElement) return null;
    el = el.parentElement;
  }
  return null;
};
